import { type ReactElement, useCallback } from 'react';
import { InView } from 'react-intersection-observer';

import { RiUserLine } from '@remixicon/react';

import {
  Flex,
  FormInput,
  FormInputIcon,
  IconSearch,
  ListBox,
  ListBoxRow,
  Pressable,
  Text,
} from '@spotify-internal/encore-web';
import { Popover, PopoverRoot } from '@spotify-internal/encore-web/next';
import { IdentityRefFragment } from '@spotify/confidence-graphql';
import {
  IdentityAvatarMui as IdentityAvatar,
  IdentityInline,
  useIdentityPickerOptions,
} from '@spotify/plugin-confidence-iam-react';

import styles from './IssueAssigneePicker.module.css';

const UNASSIGNED_ID = '__unassigned__';

export type IssueAssigneePickerProps = {
  assignee: IdentityRefFragment | null;
  autoFocusSearch?: boolean;
  disabled?: boolean;
  disableIcon?: boolean;
  onChange: (assigneeName: string | null) => void;
  renderTrigger?: (props: {
    ariaLabel: string;
    disabled: boolean;
  }) => ReactElement;
};

type IssueAssigneePickerOptionsProps = Omit<
  IssueAssigneePickerProps,
  'disabled'
> & {
  close: () => void;
};

type IssueAssigneeOptionProps = {
  identity: IdentityRefFragment;
};

const IssueAssigneeOption = ({ identity }: IssueAssigneeOptionProps) => (
  <ListBoxRow id={identity.name} textValue={identity.displayName} size="small">
    <Flex alignItems="baseline" gap="tighter-4">
      <IdentityAvatar className={styles.optionAvatar} identityRef={identity} />
      <Text as="span" variant="bodySmall">
        {identity.displayName}
      </Text>
    </Flex>
  </ListBoxRow>
);

type IssueAssigneeListProps = {
  ariaLabel: string;
  selectedKey: string;
  children: React.ReactNode;
  onSelectionChange: (keys: 'all' | Set<React.Key>) => void;
};

const IssueAssigneeList = ({
  ariaLabel,
  selectedKey,
  children,
  onSelectionChange,
}: IssueAssigneeListProps) => (
  <ListBox
    aria-label={ariaLabel}
    selectionMode="single"
    selectedKeys={new Set([selectedKey])}
    onSelectionChange={keys => {
      onSelectionChange(keys as 'all' | Set<React.Key>);
    }}
  >
    {children}
  </ListBox>
);

const IssueAssigneePickerOptions = ({
  assignee,
  autoFocusSearch,
  close,
  onChange,
}: IssueAssigneePickerOptionsProps) => {
  const {
    currentIdentityOptions,
    recentIdentityOptions,
    groupIdentityOptions,
    allIdentityOptions,
    addRecentIdentity,
    nextPage,
    hasNextPage,
    isLoading,
    isLoadingNextPage,
    search,
    setSearch,
  } = useIdentityPickerOptions();
  const hasIdentityOptions =
    currentIdentityOptions.length +
      recentIdentityOptions.length +
      groupIdentityOptions.length +
      allIdentityOptions.length >
    0;
  const isLoadingIdentities = isLoading || isLoadingNextPage;
  const selectedAssigneeKey = assignee?.name ?? UNASSIGNED_ID;

  const handleSelectionChange = useCallback(
    (keys: 'all' | Set<React.Key>) => {
      if (keys === 'all') {
        return;
      }

      const [selectedKey] = keys;
      if (!selectedKey) {
        return;
      }

      const assigneeName =
        selectedKey === UNASSIGNED_ID ? null : String(selectedKey);

      if (assigneeName) {
        addRecentIdentity(assigneeName);
      }

      setSearch('');
      onChange(assigneeName);
      close();
    },
    [addRecentIdentity, close, onChange, setSearch],
  );

  return (
    <>
      <FormInputIcon iconLeading={<IconSearch size="small" />}>
        <FormInput
          aria-label="Search identities"
          placeholder="Search identities"
          size="small"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocusSearch}
          value={search}
          onChange={event => {
            setSearch(event.currentTarget.value);
          }}
          className={styles.searchField}
         data-track-source-anchor="b789885aad"/>
      </FormInputIcon>
      <div className={styles.options}>
        <IssueAssigneeList
          ariaLabel="Assignees"
          selectedKey={selectedAssigneeKey}
          onSelectionChange={handleSelectionChange}
        >
          <ListBoxRow id={UNASSIGNED_ID} textValue="Unassigned" size="small">
            Unassigned
          </ListBoxRow>
          {currentIdentityOptions.map(identity => (
            <IssueAssigneeOption key={identity.name} identity={identity} />
          ))}
        </IssueAssigneeList>
        {recentIdentityOptions.length > 0 && (
          <section aria-label="Suggested identities">
            <Text
              as="p"
              variant="bodySmall"
              semanticColor="textSubdued"
              className={styles.sectionHeader}
            >
              Suggested
            </Text>
            <IssueAssigneeList
              ariaLabel="Suggested identities"
              selectedKey={selectedAssigneeKey}
              onSelectionChange={handleSelectionChange}
            >
              {recentIdentityOptions.map(identity => (
                <IssueAssigneeOption key={identity.name} identity={identity} />
              ))}
            </IssueAssigneeList>
          </section>
        )}
        {groupIdentityOptions.length > 0 && (
          <section aria-label="Your groups">
            <Text
              as="p"
              variant="bodySmall"
              semanticColor="textSubdued"
              className={styles.sectionHeader}
            >
              Your groups
            </Text>
            <IssueAssigneeList
              ariaLabel="Your groups"
              selectedKey={selectedAssigneeKey}
              onSelectionChange={handleSelectionChange}
            >
              {groupIdentityOptions.map(identity => (
                <IssueAssigneeOption key={identity.name} identity={identity} />
              ))}
            </IssueAssigneeList>
          </section>
        )}
        {allIdentityOptions.length > 0 && (
          <section aria-label="All users and groups">
            <Text
              as="p"
              variant="bodySmall"
              semanticColor="textSubdued"
              className={styles.sectionHeader}
            >
              All users and groups
            </Text>
            <IssueAssigneeList
              ariaLabel="All users and groups"
              selectedKey={selectedAssigneeKey}
              onSelectionChange={handleSelectionChange}
            >
              {allIdentityOptions.map(identity => (
                <IssueAssigneeOption key={identity.name} identity={identity} />
              ))}
            </IssueAssigneeList>
          </section>
        )}
        {hasNextPage && (
          <InView
            className={styles.loadMoreSentinel}
            onChange={inView => {
              if (inView && !isLoadingNextPage) {
                void nextPage();
              }
            }}
           data-track-source-anchor="67ffdae2c1;issue-assignee-picker-options-in-view"/>
        )}
      </div>
      {!isLoadingIdentities && !hasIdentityOptions && (
        <Text
          as="p"
          variant="bodySmall"
          semanticColor="textSubdued"
          className={styles.status}
        >
          No identities found
        </Text>
      )}
    </>
  );
};

export const IssueAssigneePicker = ({
  assignee,
  autoFocusSearch = true,
  disabled = false,
  disableIcon = false,
  onChange,
  renderTrigger,
}: IssueAssigneePickerProps) => {
  const assigneeLabel = assignee?.displayName ?? 'Unassigned';
  const triggerAriaLabel = `Change assignee, current value: ${assigneeLabel}`;
  const trigger = renderTrigger?.({
    ariaLabel: triggerAriaLabel,
    disabled,
  }) ?? (
    <button
      type="button"
      className={styles.trigger}
      disabled={disabled}
      aria-label={triggerAriaLabel}
     data-track-source-anchor="ef8ca118d5;issue-assignee-picker-button">
      {assignee ? (
        <IdentityInline identityRef={assignee} />
      ) : (
        <Flex alignItems="center" gap="tighter-4">
          {!disableIcon && <RiUserLine size={16} aria-hidden />}
          <Text as="span" variant="bodySmall">
            Unassigned
          </Text>
        </Flex>
      )}
    </button>
  );

  if (disabled) {
    return trigger;
  }

  return (
    <PopoverRoot>
      <Pressable>{trigger}</Pressable>
      <Popover
        aria-label="Assign issue"
        placement="bottomStart"
        hasNoArrow
        className={styles.popover}
      >
        {({ close }) => (
          <IssueAssigneePickerOptions
            assignee={assignee}
            autoFocusSearch={autoFocusSearch}
            close={close}
            onChange={onChange}
           data-track-source-anchor="5965bed45f;issue-assignee-picker-issue-assignee-picker"/>
        )}
      </Popover>
    </PopoverRoot>
  );
};
